import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
} from '@apollo/client'
import type {
  FieldPolicy,
  Reference,
  StoreObject,
  TypePolicies,
} from '@apollo/client/cache'
import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors'
import { ErrorLink } from '@apollo/client/link/error'

import { Services } from '../../services'
import { logger } from '../../utils/logger'
import { possibleTypes } from './possibleTypes'
import {
  hasPartialData,
  isAuthStatus,
  summarizeGraphQLErrors,
} from './errorLog'

const isBrowser = typeof window !== 'undefined'

interface ConnectionEdge {
  cursor?: string | null
  node?: Reference | Record<string, unknown> | null
}

interface ConnectionLike {
  edges?: readonly ConnectionEdge[]
  pageInfo?: {
    startCursor?: string | null
    endCursor?: string | null
    hasNextPage?: boolean | null
    hasPreviousPage?: boolean | null
  }
  totalCount?: number | null
}

function paginationCursor(args: Record<string, any> | null): string | null {
  // The Mapped schema nests the cursor in a `pagination: PageInfoInput`; the
  // top-level fallback keeps this usable for any connection that does not.
  const pagination = args?.pagination ?? args

  return pagination?.after ?? pagination?.before ?? null
}

/**
 * A Relay-shaped merge for connections whose cursor lives in a nested input.
 *
 * Apollo ships `relayStylePagination`, and it is nearly right — but it reads
 * `args.after` / `args.before` from the TOP level, while the Mapped schema
 * takes its cursor inside a `pagination` argument (`pointsPaginated(filter:,
 * pagination: { first, after })`). Given that shape the stock policy sees
 * neither `after` nor `before` on any page, concludes every page is a fresh
 * start, and replaces the list each time. Hence a local variant rather than a
 * wrapper around it.
 *
 * The dedupe is load-bearing rather than defensive. `fetchMore(..., {
 * updateQuery })` writes the ALREADY-merged list back through this policy, so
 * while any caller still passes `updateQuery` the merge sees pages it has
 * appended before. Keying on the node's normalized ref makes the write
 * idempotent, which is what lets a field policy and an `updateQuery` caller
 * coexist while the two land in separate changes.
 *
 * @param keyArgs - the arguments and `$variables` that identify one list.
 *   Deliberately NOT the pagination cursor: pages of one list have to share a
 *   cache entry to merge at all. Flag variables that change the SHAPE of a row
 *   do belong here — two pages fetched under different `@include` flags
 *   describe rows of different completeness, and merging them yields a list in
 *   which some rows silently lack fields the caller asked for.
 */
export function relayLikeMerge(
  keyArgs: FieldPolicy['keyArgs'] = false
): FieldPolicy<ConnectionLike | undefined> {
  return {
    keyArgs,

    merge(existing, incoming, { args, isReference, readField }) {
      if (!incoming) {
        return existing
      }

      const cursor = paginationCursor(args)

      // No cursor means this is the first page of a list rather than a slice
      // to splice into one, so it replaces what was there. Appending instead
      // would leave rows deleted server-side on screen after a refetch.
      if (!existing || !cursor) {
        return incoming
      }

      const edges: ConnectionEdge[] = []
      const seen = new Set<string>()

      const append = (list: readonly ConnectionEdge[] | undefined) => {
        for (const edge of list ?? []) {
          const node = readField('node', edge as unknown as StoreObject)
          const key = isReference(node) ? node.__ref : undefined

          if (key !== undefined) {
            if (seen.has(key)) {
              continue
            }

            seen.add(key)
          }

          edges.push(edge)
        }
      }

      append(existing.edges)
      append(incoming.edges)

      return {
        ...existing,
        ...incoming,
        edges,
        pageInfo: {
          ...existing.pageInfo,
          ...incoming.pageInfo,
          // The merged list still begins where the existing one began; only
          // its tail moved.
          startCursor:
            existing.pageInfo?.startCursor ?? incoming.pageInfo?.startCursor,
          hasPreviousPage:
            existing.pageInfo?.hasPreviousPage ??
            incoming.pageInfo?.hasPreviousPage,
        },
      }
    },
  }
}

/**
 * The `@include` flags that survive on the points documents.
 *
 * Cheap scalars (`unit`, `currentValue`, `lastUpdated`, `description`, `type`)
 * are fetched unconditionally and are absent here on purpose: a variable in
 * `keyArgs` means toggling it starts a new cached list, which for a 50k-point
 * building throws away every page already scrolled. Only the genuinely
 * expensive selections pay that price.
 */
const POINTS_SHAPE_VARIABLES = [
  '$showValueMap',
  '$showWillowTwinId',
  '$needsEquipment',
]

const pointsPaginated = relayLikeMerge(['filter', ...POINTS_SHAPE_VARIABLES])

const typePolicies: TypePolicies = {
  // Every type the grid pages points from: root `Query` for the organization
  // scope, each place type, and `Thing` for a `?thingId=` view. See
  // `POINTS_QUERIES` in the explore-grid points query builder.
  Query: { fields: { pointsPaginated } },
  Building: { fields: { pointsPaginated } },
  SubBuilding: { fields: { pointsPaginated } },
  Floor: { fields: { pointsPaginated } },
  Site: { fields: { pointsPaginated } },
  Space: { fields: { pointsPaginated } },
  Thing: { fields: { pointsPaginated } },
}

function createCoreApiCache() {
  return new InMemoryCache({ possibleTypes, typePolicies }).restore({})
}

/**
 * One cache for the browser session, and the tenant currently in it.
 *
 * A single shared cache is what makes moving between Explore tabs free, so it
 * is worth keeping — but it is module-scoped and therefore outlives every
 * React tree, every client instance and every org switch. Explore's provenance
 * view attributes rows to named connectors, which turns residue from a
 * previous tenant from an abstract concern into a visible, quotable
 * correctness bug: another org's connector names rendered beside this org's
 * rows. So the tenant that filled the cache is recorded next to it, and the
 * cache is emptied the moment that changes.
 */
const defaultCache = createCoreApiCache()
let cacheIdentity: string | null = null

function decodeJwtClaims(
  token?: string | null
): Record<string, unknown> | null {
  const segment = token?.split('.')[1]

  if (!segment) {
    return null
  }

  try {
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
    const json = isBrowser
      ? new TextDecoder().decode(
          Uint8Array.from(atob(padded), (character) => character.charCodeAt(0))
        )
      : Buffer.from(padded, 'base64').toString('utf8')

    const claims = JSON.parse(json)

    return claims && typeof claims === 'object' ? claims : null
  } catch {
    // An unreadable token is not an identity change, it is the absence of an
    // identity. Reporting null leaves the recorded tenant untouched.
    return null
  }
}

/**
 * The tenant a token grants access to, or `null` when it says nothing.
 *
 * Org id alone is not enough: an OBO session is a different tenant view of the
 * same org, and account-level impersonation can carry no org at all. These are
 * the same three claims `contexts/user.tsx` reads to answer that question.
 *
 * Deliberately decodes the payload here rather than importing `auth/auth0` —
 * that module imports this one, and a cycle through the client factory is not
 * worth the twelve lines it would save.
 */
export function coreApiIdentityKey(token?: string | null): string | null {
  const claims = decodeJwtClaims(token)

  if (!claims) {
    return null
  }

  const parts = [
    claims.sub,
    claims['https://mapped.com/org'],
    claims['https://mapped.com/obo'],
    claims['https://mapped.com/obo/account'],
  ].map((claim) => (claim == null ? '' : String(claim)))

  return parts.some(Boolean) ? parts.join(' ') : null
}

/**
 * Records the tenant a new client speaks for, emptying the shared cache when
 * that is not the tenant already in it.
 *
 * Called from `CoreApiClient` rather than only from the React provider on
 * purpose: several call sites (`auth/auth0`'s `fetchLoggedUser`,
 * `utils/mapped`) build a client directly, and a guard that only runs inside a
 * provider is one a future call site can walk around without noticing.
 * `contexts/apollo` additionally calls `clearStore()` on the outgoing client,
 * which this cannot do — that stops its in-flight watches so a departing
 * tenant's response cannot land in the arriving tenant's cache.
 *
 * @returns whether the cache was emptied.
 */
export function noteCoreApiIdentity(token?: string | null): boolean {
  if (!isBrowser) {
    return false
  }

  const next = coreApiIdentityKey(token)

  // No decodable identity (signed out, a service call, a malformed token) is
  // not evidence of a switch, and treating it as one would discard a warm
  // cache on every anonymous request.
  if (next === null || next === cacheIdentity) {
    return false
  }

  const hadIdentity = cacheIdentity !== null
  cacheIdentity = next

  if (!hadIdentity) {
    return false
  }

  // `discardWatches: false` because the live client keeps its watches and its
  // queries have to notice that their data is gone and refetch; discarding
  // them would leave the screen showing the previous tenant's rows.
  void defaultCache.reset({ discardWatches: false })

  return true
}

const errorLink = new ErrorLink(({ error, operation }) => {
  if (CombinedGraphQLErrors.is(error)) {
    // One line per operation, never one per error: a single page of rows can
    // carry hundreds of identical field errors (see `summarizeGraphQLErrors`).
    const summary = summarizeGraphQLErrors(error.errors)
    const first = summary.messages[0]?.message ?? 'unknown error'
    const more = summary.total > 1 ? ` (+${summary.total - 1} more)` : ''
    const log = logger
      .with()
      .any('data', { errorCount: summary.total, errors: summary.messages })
      .logger()
    const line = `GraphQL operation "${operation.operationName}" returned an error: ${first}${more}`

    if (hasPartialData(error.data)) {
      log.warn(line)
    } else {
      log.error(line)
    }
    // A cancelled request (e.g. the user aborting a background export) is
    // not a real network failure — don't ship it to logging.
  } else if (!operation.getContext().suppressNetworkErrorLog) {
    const log = logger.with().error(error).logger()
    const line = `GraphQL request "${operation.operationName}" failed with a network error`

    // An expired session is the caller's state, not a Console fault.
    if (ServerError.is(error) && isAuthStatus(error.statusCode)) {
      log.warn(line)
    } else {
      log.error(line)
    }
  }
})

// Shared HTTPS agent for server-side BFF calls — enables TCP keep-alive so
// connections to the GraphQL API are reused across requests instead of
// opening a new TLS handshake on every call.
const serverHttpsAgent = !isBrowser
  ? new (require('https') as typeof import('https')).Agent({ keepAlive: true })
  : undefined

export function CoreApiClient(token?: string, tokenType: string = 'Bearer') {
  noteCoreApiIdentity(token)

  const httpLink = new HttpLink({
    uri: Services.graphql_api.uri,
    credentials: 'omit',
    // `agent` is a node-fetch extension not in browser RequestInit types — cast needed
    ...(serverHttpsAgent
      ? { fetchOptions: { agent: serverHttpsAgent } as RequestInit }
      : {}),
  })

  const authMiddleware = new ApolloLink((operation, forward) => {
    if (!token) {
      return forward(operation)
    }

    operation.setContext({
      headers: {
        Authorization: `${tokenType} ${token}`,
      },
    })

    return forward(operation)
  })

  return new ApolloClient({
    // Every server render gets its own cache: the module-scoped one would be
    // shared across concurrent requests from different tenants.
    cache: isBrowser ? defaultCache : createCoreApiCache(),
    link: ApolloLink.from([errorLink, authMiddleware, httpLink]),
  })
}
